import { Typography } from "antd";
import dayjs from "dayjs";
import { FC } from "react";
import { Contract } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { PageBlock } from "@/shared/pageComposition/BlockList";
import BaseButton from "@/shared/ui/BaseButton";
import { PrimaryText } from "@/shared/ui/Typography";

import {
    StyledAPContractsTable,
    StyledAPContractsTableRow,
    StyledAPContractsTableRowLeft,
} from "../../Styled";
import { InfoTabContractsCapabilities } from "./InfoTabContracts.policy";

export interface IEnrichedContract extends Contract {
    isExpired: boolean;
}

export const isContractExpired = (
    endDate: string | null | undefined,
): boolean => {
    return endDate ? dayjs(endDate).isBefore(dayjs(), "day") : false;
};

const ContractRow: FC<{
    contract: IEnrichedContract;
    pointId: string;
    contractTypeName: string;
}> = ({ contract, pointId, contractTypeName }) => {
    const { id, name, endDate, isExpired } = contract;

    return (
        <StyledAPContractsTableRow
            key={id}
            to={buildRoutePath(ROUTES.PointContractDetail, {
                pointId,
                contractId: id,
            })}
        >
            <StyledAPContractsTableRowLeft $isExpired={isExpired}>
                <PrimaryText fontSize={14} fontWeight="400">
                    {name}
                </PrimaryText>
                <Typography.Text type="secondary">
                    {contractTypeName}
                </Typography.Text>
            </StyledAPContractsTableRowLeft>

            <PrimaryText
                fontSize={14}
                fontWeight="400"
                style={{ maxWidth: "100px" }}
            >
                {endDate
                    ? `до ${dayjs(endDate).format("DD.MM.YYYY")}`
                    : "Безстроково"}
            </PrimaryText>
        </StyledAPContractsTableRow>
    );
};

export type InfoTabContractsModel = {
    capabilities: InfoTabContractsCapabilities;
    pointId: string;
    enrichedContracts: IEnrichedContract[];
    contractTypesList?: Record<string, string>;
    onAddContract: () => void;
};

const ContractsListBlock: FC<{
    pointId: string;
    enrichedContracts: IEnrichedContract[];
    contractTypesList?: Record<string, string>;
}> = ({ pointId, enrichedContracts, contractTypesList }) => {
    const hasContracts = enrichedContracts.length > 0;

    if (!hasContracts) {
        return (
            <PrimaryText
                fontSize={14}
                fontWeight="400"
                style={{ textAlign: "center" }}
            >
                Контрактів по даній ТТ не знайдено
            </PrimaryText>
        );
    }

    return (
        <StyledAPContractsTable>
            {enrichedContracts.map((contract) => (
                <ContractRow
                    key={contract.id}
                    contract={contract}
                    pointId={pointId}
                    contractTypeName={
                        contractTypesList?.[contract.type] ||
                        "Невідомий вид контракту"
                    }
                />
            ))}
        </StyledAPContractsTable>
    );
};

const AddContractButtonBlock: FC<{ onAddContract: () => void }> = ({
    onAddContract,
}) => (
    <BaseButton onClick={onAddContract} type="primary" marginTop="16px">
        Додати контракт
    </BaseButton>
);

export const getInfoTabContractsBlocks = (
    model: InfoTabContractsModel,
): PageBlock<InfoTabContractsModel>[] => [
    {
        key: "contracts-list",
        order: 10,
        render: () => (
            <ContractsListBlock
                pointId={model.pointId}
                enrichedContracts={model.enrichedContracts}
                contractTypesList={model.contractTypesList}
            />
        ),
    },
    {
        key: "add-contract-button",
        order: 20,
        isVisible: (model) => model.capabilities.canAddContract,
        render: () => (
            <AddContractButtonBlock onAddContract={model.onAddContract} />
        ),
    },
];
